// PortalApplications: the candidate's applications on the CRM layout. Each one
// is a card with its progress rail; the side rail says what each stage means
// and offers Lexa to prepare for the next interview. Candidate-safe fields
// only (GET /api/portal/applications).
// P2-PORTAL (22 Oct 2026)
import { useState, useEffect, useCallback } from 'react';
import API_BASE from '../../../config';
import { Ico, ICONS, ukDate, workPattern } from './portalKit';

const STAGES = ['applied', 'under_review', 'submitted_to_client', 'interview', 'offer', 'placed'];
const STAGE_LABEL = { applied: 'Applied', under_review: 'Under review', submitted_to_client: 'Submitted to employer', interview: 'Interview', offer: 'Offer', placed: 'Placed' };
const STAGE_MEANS = [
  ['Applied', 'We have your application.'],
  ['Under review', 'Someone at Generative Talent Co is reading it.'],
  ['Submitted to employer', 'We have put your profile in front of them.'],
  ['Interview', 'The employer wants to meet you.'],
  ['Offer', 'They have offered you the job.'],
  ['Placed', 'You have started. Congratulations.'],
];
const PREP_STAGES = ['applied', 'under_review', 'submitted_to_client', 'interview'];

export default function PortalApplications({ getToken, go }) {
  const [state, setState] = useState('loading');
  const [applications, setApplications] = useState([]);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await fetch(API_BASE + '/api/portal/applications', { headers: { Authorization: 'Bearer ' + token } });
      if (!r.ok) { setState('error'); return; }
      const data = await r.json();
      setApplications(data.applications || []);
      setState('ready');
    } catch (e) { setState('error'); }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const next = applications.find((a) => PREP_STAGES.indexOf(a.status) !== -1);

  return (
    <div className="gtr2-body">
      <div className="gtr2-main">
        <div className="gtr-card gtr2-tabcard">
          <div className="gtp2-cardhead">
            <div><b>Your applications</b><span>We keep you posted by email as each one moves.</span></div>
            {state === 'ready' && <span className="gtr2-count">{applications.length}</span>}
          </div>
          <div className="gtr2-pad gtp2-stack">
            {state === 'loading' && <div className="gtr2-empty">Loading your applications</div>}
            {state === 'error' && <div className="gtr2-empty">We could not load your applications just now. Try again shortly.</div>}
            {state === 'ready' && applications.length === 0 && (
              <div className="gtr2-soon">
                <span className="gtr2-soon-ico"><Ico d={ICONS.doc} /></span>
                <b>No applications yet</b>
                <p>When you apply for a role, it shows here with where it stands.</p>
                <button type="button" className="gtr2-btn gtr2-btn--primary gtp2-mt" onClick={() => go('Jobs')}>Browse open roles</button>
              </div>
            )}
            {state === 'ready' && applications.map((a) => (
              <article key={a.ref} className={'gtp2-app' + (a.status === 'closed' ? ' gtp2-app--closed' : '')}>
                <div className="gtp2-app-head">
                  <span className="gtr2-listico"><Ico d={ICONS.brief} size={15} /></span>
                  <div className="gtp2-app-id">
                    <b>{a.role.title}</b>
                    <span>{[a.role.location, workPattern(a.role.work_pattern), a.ref].filter(Boolean).join(' · ')}</span>
                  </div>
                  <span className={'gtp2-tag gtp2-tag--' + a.status}>{a.status_label}</span>
                </div>
                {a.status !== 'closed' && <ProgressRail status={a.status} />}
                <div className="gtp2-app-foot">
                  <span>Applied {ukDate(a.applied_at)}</span>
                  {PREP_STAGES.indexOf(a.status) !== -1 && (
                    <button type="button" className="gtp2-linkbtn gtp2-linkbtn--lexa" onClick={() => go('Lexa', { kind: 'interview', role: a.role.ref })}>
                      <Ico d={ICONS.spark} />Practise this interview with Lexa
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      <aside className="gtr2-side">
        <div className="gtr-card">
          <div className="gtr-cardh">What each stage means</div>
          <div className="gtr-props">
            {STAGE_MEANS.map(([k, v]) => (
              <div key={k} className="gtr-prop gtp2-prop-stack"><span className="gtr-prop-k">{k}</span><span className="gtr-prop-v">{v}</span></div>
            ))}
          </div>
        </div>
        <div className="gtr-card gtp2-sidelexa">
          <div className="gtp2-sidelexa-h"><span className="gtlx-orb" aria-hidden="true" /><b>Walk in prepared</b></div>
          <p>Lexa runs a mock interview built from the role and your CV, then writes up every answer with a stronger way to put it.</p>
          <button type="button" className="gtr2-btn gtp2-btn--lexa" onClick={() => go('Lexa', next ? { kind: 'interview', role: next.role.ref } : null)}>
            {next ? 'Practise for ' + next.role.title : 'Open Lexa'}
          </button>
        </div>
      </aside>
    </div>
  );
}

function ProgressRail({ status }) {
  const idx = STAGES.indexOf(status);
  return (
    <ol className="gtp2-rail">
      {STAGES.map((s, i) => (
        <li key={s} className={'gtp2-rstep gtp2-rstep--' + (i < idx ? 'done' : i === idx ? 'now' : 'todo')}>
          <span className="gtp2-rdot" aria-hidden="true">{i < idx ? <Ico d={ICONS.check} size={10} /> : null}</span>
          <span className="gtp2-rlabel">{STAGE_LABEL[s]}</span>
        </li>
      ))}
    </ol>
  );
}
